import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  forwardRef,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faFilter,
  faChartPieSimple,
  faChartMixed,
  faCaretDown,
} from '@fortawesome/pro-solid-svg-icons';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-custom-select',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule],
  templateUrl: './custom-select.component.html',
  styleUrls: ['./custom-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomSelectComponent),
      multi: true,
    },
  ],
})
export class CustomSelectComponent implements ControlValueAccessor {
  @Input() options: SelectOption[] = [];
  @Input() placeholder = 'Seleccionar...';
  @Input() searchPlaceholder = 'Buscar...';
  @Input() small = false;
  @Input() icon: IconProp = faChartMixed;
  @Input() showIcon = true;
  @Input() loading = false;
  @Input() loadingMore = false;
  @Input() showSearch = false;
  @Input() hasMore = false;

  @Output() selectionChange = new EventEmitter<string>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() loadMore = new EventEmitter<void>();

  @ViewChild('button') buttonRef!: ElementRef<HTMLButtonElement>;
  @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('optionsList') optionsListRef?: ElementRef<HTMLUListElement>;

  faFilter = faFilter;
  faChartPieSimple = faChartPieSimple;
  faChartMixed = faChartMixed;
  faCaretDown = faCaretDown;

  isOpen = false;
  dropUp = false;
  focusedIndex = -1;
  value = '';
  isDisabled = false;
  searchTerm = '';

  // ControlValueAccessor
  private onChange?: (value: string) => void;
  private onTouched?: () => void;

  writeValue(obj: string | null): void {
    this.value = obj ?? '';
  }
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = Boolean(isDisabled);
  }

  // Donde antes hacías: this.onChange(value)
  private emitChange(value: string): void {
    this.onChange?.(value);
  }

  // Donde antes hacías: this.onTouched()
  private emitTouched(): void {
    this.onTouched?.();
  }

  toggle(event?: Event) {
    if (event) event.stopPropagation();
    if (this.isDisabled) return;

    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.focusedIndex = this.options.findIndex((o) => o.value === this.value);
      this.dropUp = this.shouldDropUp();
      setTimeout(() => {
        this.searchInputRef?.nativeElement.focus();
      }, 0);
    } else {
      this.searchTerm = '';
      this.searchChange.emit('');
    }
  }

  select(option: SelectOption) {
    if (option.disabled) return;
    this.value = option.value;
    this.emitChange(this.value);
    this.selectionChange.emit(this.value);
    this.close();
  }

  close() {
    this.isOpen = false;
    this.searchTerm = '';
    this.emitTouched();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(e: Event) {
    const el = this.buttonRef?.nativeElement;
    const list = this.optionsListRef?.nativeElement;
    const input = this.searchInputRef?.nativeElement;

    if (!el) return;

    const target = e.target as Node;
    if (!el.contains(target) && !list?.contains(target) && !input?.contains(target)) {
      this.isOpen = false;
    }
  }

  onKeydown(e: KeyboardEvent) {
    if (!this.isOpen && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      this.toggle();
      return;
    }

    if (this.isOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.focusedIndex = Math.min(this.focusedIndex + 1, this.options.length - 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.focusedIndex = Math.max(this.focusedIndex - 1, 0);
      } else if (e.key === 'Enter' || e.key === ' ') {
        // Only select if not focused on search input or if enter is pressed
        if (document.activeElement !== this.searchInputRef?.nativeElement || e.key === 'Enter') {
          e.preventDefault();
          const opt = this.options[this.focusedIndex];
          if (opt) this.select(opt);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      }
    }
  }

  onScroll(event: Event) {
    const el = event.target as HTMLElement;
    if (!el || !this.hasMore || this.loadingMore) return;

    const threshold = 10;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
      this.loadMore.emit();
    }
  }

  onSearch(event: Event) {
    const term = (event.target as HTMLInputElement).value;
    this.searchTerm = term;
    this.searchChange.emit(term);
    this.focusedIndex = -1;
  }

  private getClippingAncestor(el: HTMLElement): HTMLElement | null {
    let node = el.parentElement;
    while (node && node !== document.body) {
      const style = getComputedStyle(node);
      if (/(auto|scroll|hidden)/.test(style.overflowY)) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  private shouldDropUp(): boolean {
    const button = this.buttonRef?.nativeElement;
    if (!button) return false;

    const estimatedDropdownHeight = 340;
    const buttonRect = button.getBoundingClientRect();
    const clippingAncestor = this.getClippingAncestor(button);
    const boundaryBottom = clippingAncestor
      ? clippingAncestor.getBoundingClientRect().bottom
      : window.innerHeight;
    const boundaryTop = clippingAncestor ? clippingAncestor.getBoundingClientRect().top : 0;

    const spaceBelow = boundaryBottom - buttonRect.bottom;
    const spaceAbove = buttonRect.top - boundaryTop;

    return spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow;
  }

  labelFor(value: string) {
    return this.options.find((o) => o.value === value)?.label || this.placeholder || '';
  }
}
